import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import EmailToCpaDialog from '../components/EmailToCpaDialog';
import { CpaShareStrip, useCpaSends } from '../components/CpaShareHistory';
import { useOnboarding, ModuleTips } from '../components/onboarding';
import { useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import DOMPurify from 'dompurify';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Trash2, Download, Loader2, MessageCircle, Sparkles, Search, Database, TrendingUp, DollarSign, User, CheckCircle, Shield, ArrowRight, ExternalLink, BookOpen, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown } from 'lucide-react';

// Advisor photos (shared by advisor grid + message headers)
const ADVISOR_PHOTOS = {
  carlotta: 'https://images.unsplash.com/photo-1770364020204-102331b625ea?w=400&h=400&fit=crop',
  justin: 'https://images.unsplash.com/photo-1752118465028-4a135f89e474?w=400&h=400&fit=crop',
  sabrina: 'https://images.unsplash.com/photo-1736939681295-bb2e6759dddc?w=400&h=400&fit=crop',
  david: 'https://images.unsplash.com/photo-1680540692052-79fde1108370?w=400&h=400&fit=crop',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── "Was this helpful?" Feedback Bar ─────────────────────────────────────────
const FeedbackBar = ({ messageId, apiHeaders }) => {
  const [voted, setVoted] = React.useState(null); // 'helpful' | 'unhelpful' | null

  const handleVote = async (rating) => {
    if (voted) return; // already voted
    setVoted(rating);
    try {
      await axios.post(
        `${API}/chat/feedback`,
        { message_id: messageId, rating },
        { headers: apiHeaders }
      );
    } catch {
      // Non-critical — don't surface errors to user
    }
  };

  return (
    <div className="mt-2 flex items-center gap-3" data-testid="feedback-bar">
      <span className="text-xs text-stone-400">Was this helpful?</span>
      <button
        onClick={() => handleVote('helpful')}
        disabled={!!voted}
        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-all ${
          voted === 'helpful'
            ? 'bg-emerald-100 border-emerald-300 text-emerald-700'
            : 'border-stone-200 text-stone-400 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50'
        } disabled:cursor-default`}
        data-testid="feedback-thumbs-up"
      >
        <ThumbsUp className="w-3 h-3" />
        {voted === 'helpful' && <span>Yes!</span>}
      </button>
      <button
        onClick={() => handleVote('unhelpful')}
        disabled={!!voted}
        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-all ${
          voted === 'unhelpful'
            ? 'bg-red-50 border-red-300 text-red-600'
            : 'border-stone-200 text-stone-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50'
        } disabled:cursor-default`}
        data-testid="feedback-thumbs-down"
      >
        <ThumbsDown className="w-3 h-3" />
        {voted === 'unhelpful' && <span>Thanks</span>}
      </button>
    </div>
  );
};
const SourcesPanel = ({ sources }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!sources || sources.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-stone-100" data-testid="irs-sources-panel">
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-emerald-600 transition-colors group"
        data-testid="sources-toggle"
      >
        <BookOpen className="w-3.5 h-3.5 group-hover:text-emerald-500" />
        <span className="font-medium">IRS Sources ({sources.length})</span>
        {expanded
          ? <ChevronUp className="w-3 h-3" />
          : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5" data-testid="sources-list">
          {sources.map((src, i) => (
            <a
              key={i}
              href={src.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50 border border-emerald-100 hover:border-emerald-300 hover:bg-emerald-50/80 transition-colors group"
              data-testid={`source-${i}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-emerald-700 group-hover:text-emerald-800 truncate">
                  {src.pub_short}
                </p>
                <p className="text-xs text-stone-500 truncate">{src.section}</p>
              </div>
              <ExternalLink className="w-3 h-3 text-emerald-400 flex-shrink-0 mt-0.5 group-hover:text-emerald-600" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const Chat = () => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const { triggerAction } = useOnboarding();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [queryMode, setQueryMode] = useState('advisor'); // 'advisor' or 'data'
  const [advisors, setAdvisors] = useState([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);  // null = show all advisors
  const [showAdvisorSelect, setShowAdvisorSelect] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Add animation styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .animate-fade-in {
        animation: fadeInUp 0.6s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Translated suggested questions
  const SUGGESTED_QUESTIONS = [
    t('chat.suggested_questions.deductions_missing'),
    t('chat.suggested_questions.scorp_conversion'),
    t('chat.suggested_questions.home_office'),
    t('chat.suggested_questions.retirement'),
    t('chat.suggested_questions.vehicle_expenses'),
    t('chat.suggested_questions.hire_family')
  ];

  const DATA_QUERY_EXAMPLES = [
    t('chat.data_queries.travel_spending'),
    t('chat.data_queries.total_income', { year: '2024' }),
    t('chat.data_queries.highest_category'),
    t('chat.data_queries.effective_rate'),
    t('chat.data_queries.year_comparison'),
    t('chat.data_queries.transaction_count')
  ];

  const fetchAdvisors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/advisors`);
      setAdvisors(response.data.advisors);
    } catch (error) {
      // Advisors unavailable
    }
  }, []);

  const scrollToBottom = useCallback((force = false) => {
    // Only auto-scroll if user is near the bottom or forced
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
        if (isNearBottom || force) {
          // Use requestAnimationFrame to prevent scroll jump during render
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          });
        }
      }
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/history`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      const history = response.data.map(h => ([
        { role: 'user', content: h.user_message, advisorId: h.advisor_id },
        {
          role: 'assistant',
          content: h.ai_response,
          advisorId: h.advisor_id,
          sources: h.sources || [],
          message_id: h.message_id || null,
        }
      ])).flat();
      setMessages(history);
    } catch (error) {
      // Chat history unavailable
    } finally {
      setHistoryLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchHistory();
    fetchAdvisors();
  }, [fetchAdvisors, fetchHistory]);

  // Handle prefilled messages from navigation (e.g., from Strategy page)
  useEffect(() => {
    if (location.state?.prefillMessage) {
      setInput(location.state.prefillMessage);
    }
  }, [location.state]);

  useEffect(() => {
    // Only scroll to bottom when loading finishes and there are messages
    if (!loading && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      // Only scroll after AI responds, not during typing
      if (lastMessage.role === 'assistant') {
        // Delay scroll to allow DOM to settle
        setTimeout(() => scrollToBottom(false), 100);
      }
    }
  }, [loading, messages.length, scrollToBottom]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, mode: queryMode, advisorId: selectedAdvisor }]);
    setLoading(true);

    try {
      let response;
      
      if (queryMode === 'data') {
        // Natural language data query
        response = await axios.post(`${API}/ai/query`, 
          { question: userMessage },
          { headers: getAuthHeaders(), withCredentials: true }
        );
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: response.data.answer,
          mode: 'data',
          advisorId: selectedAdvisor,
          dataYear: response.data.data_year
        }]);
      } else {
        // Streaming advisor chat — uses SSE for real-time token display
        const tempId = `streaming_${Date.now()}`;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '',
          advisorId: selectedAdvisor,
          sources: [],
          message_id: null,
          isStreaming: true,
          statusMsg: 'Thinking...',
          tempId,
        }]);

        let streamFailed = false;
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ message: userMessage, advisor_id: selectedAdvisor, use_rag: true }),
          });

          if (!res.ok) throw new Error(`Stream error ${res.status}`);

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop(); // keep incomplete last line

            for (const line of lines) {
              if (!line.startsWith('data: ')) continue;
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'status') {
                    const update = { statusMsg: evt.message };
                    if (evt.sources_preview && evt.sources_preview.length > 0) {
                      update.sources_preview = evt.sources_preview;
                    }
                    setMessages(prev => prev.map(m =>
                      m.tempId === tempId ? { ...m, ...update } : m
                    ));
                  } else if (evt.type === 'token') {
                  setMessages(prev => prev.map(m =>
                    m.tempId === tempId ? { ...m, content: m.content + evt.content } : m
                  ));
                } else if (evt.type === 'done') {
                  setMessages(prev => prev.map(m =>
                    m.tempId === tempId
                      ? { ...m, sources: evt.sources || [], message_id: evt.message_id, isStreaming: false, tempId: undefined }
                      : m
                  ));
                } else if (evt.type === 'error') {
                  streamFailed = true;
                }
              } catch { /* malformed SSE line — skip */ }
            }
          }
        } catch {
          streamFailed = true;
        }

        if (streamFailed) {
          // Fallback to non-streaming on error
          setMessages(prev => prev.filter(m => m.tempId !== tempId));
          const fallback = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chat`,
            { message: userMessage, advisor_id: selectedAdvisor, use_rag: true },
            { headers: getAuthHeaders(), withCredentials: true }
          );
          setMessages(prev => [...prev, {
            role: 'assistant', content: fallback.data.response, advisorId: selectedAdvisor,
            sources: fallback.data.sources || [], message_id: fallback.data.message_id,
          }]);
        }

        triggerAction('send_chat_message');
        if (userMessage.toLowerCase().includes('strategy') || userMessage.toLowerCase().includes('s-corp') || userMessage.toLowerCase().includes('deduction')) {
          triggerAction('ask_strategy_question');
        }
      }
    } catch (error) {
      toast.error(t('chat.failed_to_respond'));
      setMessages(prev => prev.slice(0, -1)); // Remove the user message if failed
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestionClick = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const [exporting, setExporting] = useState(false);
  const [cpaSendsKey, setCpaSendsKey] = useState(0);
  const cpaSends = useCpaSends(selectedAdvisor, cpaSendsKey);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/chat/history/export`, {
        params: { advisor_id: selectedAdvisor }, responseType: 'blob', withCredentials: true,
      });
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'solelytax-chat.pdf';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Chat exported as PDF');
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'No saved conversation with this advisor yet' : 'Could not export chat');
    } finally {
      setExporting(false);
    }
  };

  const handleClearHistory = async () => {
    // Confirmation happens in an AlertDialog (a toast over the button used to intercept the 2nd click)

    try {
      await axios.delete(`${API}/chat/history`, {
        params: { advisor_id: selectedAdvisor },
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMessages(prev => prev.filter(m => m.advisorId !== selectedAdvisor));
      toast.success(t('chat.history_cleared'));
    } catch (error) {
      toast.error(t('chat.failed_to_clear_history'));
    }
  };

  const formatMessage = (content) => {
    // Escape raw HTML so text like "<script>" displays literally instead of being stripped
    const escaped = String(content ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped
      .split('\n')
      .map((line, i) => {
        // Bold, then single-asterisk italics
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        line = line.replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, '$1<em>$2</em>');
        // H4 headings (#### )
        if (line.trim().startsWith('#### ')) {
          return <p key={i} className="font-semibold text-stone-800 text-sm mb-1 mt-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.trim().slice(5)) }} />;
        }
        // H3 headings (### )
        if (line.trim().startsWith('### ')) {
          return <p key={i} className="font-bold text-stone-900 text-sm mb-1 mt-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.trim().slice(4)) }} />;
        }
        // H2 headings (## )
        if (line.trim().startsWith('## ')) {
          return <p key={i} className="font-bold text-stone-900 text-base mb-1 mt-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.trim().slice(3)) }} />;
        }
        // Bullet points (- or • or *)
        if (line.trim().startsWith('- ') || line.trim().startsWith('• ') || line.trim().startsWith('* ')) {
          return <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.trim().slice(2)) }} />;
        }
        // Numbered lists
        if (/^\d+\.\s/.test(line.trim())) {
          return <li key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.replace(/^\d+\.\s/, '')) }} />;
        }
        return <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line) }} className={line ? 'mb-2' : 'mb-1'} />;
      });
  };

  const getAdvisorInfo = (advisorId) => {
    return advisors.find(a => a.id === advisorId) || advisors[0] || { name: 'AI Advisor', avatar_color: '#8B5CF6' };
  };

  const currentAdvisor = getAdvisorInfo(selectedAdvisor);
  const advisorMessages = messages.filter(m => !m.advisorId || !selectedAdvisor || m.advisorId === selectedAdvisor);

  if (historyLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="h-[calc(100vh-6rem)] flex flex-col" data-testid="chat-page">
        {/* Show Advisor Grid if none selected */}
        {!selectedAdvisor ? (
          <div className="flex-1 overflow-auto p-4 sm:p-6 bg-gradient-to-br from-slate-50 to-white">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 animate-fade-in">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
                  Choose Your AI Tax Advisor
                </h1>
                <p className="text-lg sm:text-xl text-gray-600">
                  Get personalized guidance from our team of tax and financial experts
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
                {advisors.map((advisor, index) => {
                  const colorThemes = {
                    carlotta: {
                      gradient: 'from-emerald-50 to-white',
                      ring: 'ring-emerald-500',
                      button: 'bg-emerald-600 hover:bg-emerald-700',
                      glow: 'hover:shadow-emerald-200',
                      title: 'text-emerald-700',
                      photo: 'https://images.unsplash.com/photo-1770364020204-102331b625ea?w=400&h=400&fit=crop'
                    },
                    justin: {
                      gradient: 'from-blue-50 to-white',
                      ring: 'ring-blue-500',
                      button: 'bg-blue-600 hover:bg-blue-700',
                      glow: 'hover:shadow-blue-200',
                      title: 'text-blue-700',
                      photo: 'https://images.unsplash.com/photo-1752118465028-4a135f89e474?w=400&h=400&fit=crop'
                    },
                    sabrina: {
                      gradient: 'from-purple-50 to-white',
                      ring: 'ring-purple-500',
                      button: 'bg-purple-600 hover:bg-purple-700',
                      glow: 'hover:shadow-purple-200',
                      title: 'text-purple-700',
                      photo: 'https://images.unsplash.com/photo-1736939681295-bb2e6759dddc?w=400&h=400&fit=crop'
                    },
                    david: {
                      gradient: 'from-amber-50 to-white',
                      ring: 'ring-amber-500',
                      button: 'bg-amber-600 hover:bg-amber-700',
                      glow: 'hover:shadow-amber-200',
                      title: 'text-amber-700',
                      photo: 'https://images.unsplash.com/photo-1680540692052-79fde1108370?w=400&h=400&fit=crop'
                    }
                  };
                  
                  const theme = colorThemes[advisor.id] || colorThemes.carlotta;
                  
                  const sampleQuestions = {
                    carlotta: [
                      'How can I reduce my tax bill for next year?',
                      'Should I elect S-Corp status?',
                      'What home office deductions can I claim?'
                    ],
                    justin: [
                      'How do I price my services for maximum profit?',
                      'What is a healthy profit margin?',
                      'How much should I save for quarterly taxes?'
                    ],
                    sabrina: [
                      'How should I organize my receipts?',
                      'What records do I need for IRS compliance?',
                      'How do I categorize this expense?'
                    ],
                    david: [
                      'Should I form an LLC or stay sole proprietor?',
                      'What is the difference between LLC and S-Corp?',
                      'Do I need a business license?'
                    ]
                  };
                  
                  return (
                    <Card
                      key={advisor.id}
                      data-testid={`advisor-card-${advisor.id}`}
                      onClick={() => setSelectedAdvisor(advisor.id)}
                      className={`cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] hover:-translate-y-2 ${theme.glow} border-2 hover:border-gray-300 group bg-gradient-to-br ${theme.gradient}`}
                      style={{
                        animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`
                      }}
                    >
                      <CardContent className="p-6 sm:p-8">
                        {/* Avatar with Gradient Ring */}
                        <div className="flex justify-center mb-6">
                          <div className={`relative`}>
                            <div className={`absolute inset-0 ${theme.ring} ring-4 rounded-full blur-sm opacity-50`}></div>
                            <img
                              src={theme.photo}
                              alt={advisor.name}
                              className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-xl"
                            />
                          </div>
                        </div>
                        
                        {/* Name & Title */}
                        <div className="text-center mb-4">
                          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                            {advisor.name}
                          </h3>
                          <p className={`text-lg sm:text-xl font-semibold ${theme.title} mb-2`}>
                            {advisor.title}
                          </p>
                          <p className="text-base text-gray-600 italic">
                            &ldquo;{advisor.tagline}&rdquo;
                          </p>
                        </div>
                        
                        {/* Specialty */}
                        <p className="text-sm text-gray-700 text-center mb-4 px-2">
                          {advisor.specialty}
                        </p>
                        
                        {/* Expertise with Icons */}
                        <div className="flex flex-wrap justify-center gap-2 mb-6">
                          {advisor.expertise.map((exp, i) => (
                            <Badge key={i} variant="secondary" className="text-xs sm:text-sm px-3 py-1 bg-white/80">
                              {exp}
                            </Badge>
                          ))}
                        </div>
                        
                        {/* Sample Questions */}
                        <div className="mb-6 bg-white/60 rounded-lg p-4 border border-gray-200">
                          <p className="text-xs font-semibold text-gray-700 mb-2">Ask me about:</p>
                          <ul className="space-y-1">
                            {sampleQuestions[advisor.id]?.map((q, i) => (
                              <li key={i} className="text-xs text-gray-600 flex items-start gap-1">
                                <span className="text-emerald-500 flex-shrink-0">•</span>
                                <span>{q}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* CTA Button */}
                        <Button 
                          className={`w-full ${theme.button} text-white py-6 text-lg font-bold shadow-lg group-hover:shadow-xl transition-all`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAdvisor(advisor.id);
                          }}
                        >
                          <MessageCircle className="w-5 h-5 mr-2" />
                          Chat with {advisor.name.split(' ')[0]}
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Trust Badge */}
              <div className="mt-12 text-center">
                <p className="text-sm text-gray-500">
                  <Shield className="w-4 h-4 inline mr-1" />
                  All advisors enhanced with current IRS tax code and 2024/2025 regulations
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Existing chat interface when advisor is selected */}
        {/* Advisor Selection Panel */}
        {showAdvisorSelect && (
          <div className="mb-4 p-4 bg-white rounded-lg border shadow-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-stone-800">{t('chat.choose_advisor')}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAdvisorSelect(false)}>×</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {advisors.map(advisor => (
                <div
                  key={advisor.id}
                  data-testid={`advisor-option-${advisor.id}`}
                  onClick={() => { setSelectedAdvisor(advisor.id); setShowAdvisorSelect(false); }}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                    selectedAdvisor === advisor.id 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-stone-200 hover:border-stone-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback style={{ backgroundColor: advisor.avatar_color }} className="text-white font-bold">
                        {advisor.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-stone-800 truncate">{advisor.name}</p>
                      <p className="text-xs text-stone-500 truncate">{advisor.title}</p>
                    </div>
                    {selectedAdvisor === advisor.id && (
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-stone-600 mb-2 line-clamp-2">{advisor.specialty}</p>
                  <div className="flex flex-wrap gap-1">
                    {advisor.expertise.slice(0, 2).map((exp, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{exp}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 bg-white p-3 rounded-lg border border-stone-200">
          <div 
            className="flex items-center gap-3 sm:gap-4 min-w-0 cursor-pointer hover:bg-stone-50 p-2 rounded-lg transition-colors"
            onClick={() => setShowAdvisorSelect(!showAdvisorSelect)}
            data-testid="advisor-switcher"
          >
            <Avatar className="w-10 h-10 border-2 border-emerald-500">
              <AvatarFallback style={{ backgroundColor: currentAdvisor?.avatar_color || '#8B5CF6' }} className="text-white font-bold text-lg">
                {currentAdvisor?.name?.split(' ').map(n => n[0]).join('') || 'AI'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-base sm:text-lg font-bold text-stone-800 leading-tight">{currentAdvisor?.name || 'AI Advisor'}, {currentAdvisor?.title || ''}</h1>
              <p className="text-sm text-stone-600">{currentAdvisor?.specialty || 'Your AI Tax Strategist'} <span className="text-emerald-600 font-medium">▼ {t('chat.change_advisor')}</span></p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Mode Toggle */}
            <div className="flex bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setQueryMode('advisor')}
                data-testid="mode-advisor-btn"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  queryMode === 'advisor' 
                    ? 'bg-emerald-500 text-white' 
                    : 'text-stone-600 hover:bg-stone-200'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                {t('chat.tax_advisor_mode')}
              </button>
              <button
                onClick={() => setQueryMode('data')}
                data-testid="mode-data-btn"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  queryMode === 'data' 
                    ? 'bg-blue-500 text-white' 
                    : 'text-stone-600 hover:bg-stone-200'
                }`}
              >
                <Database className="w-4 h-4" />
                {t('chat.data_query_mode')}
              </button>
            </div>
            
            {advisorMessages.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPdf}
                disabled={exporting}
                className="border-stone-300 text-stone-700 hover:bg-stone-100"
                data-testid="export-chat-pdf-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Export PDF
              </Button>
            )}
            {advisorMessages.length > 0 && (
              <EmailToCpaDialog advisorId={selectedAdvisor} advisorName={currentAdvisor?.name || 'this advisor'} sends={cpaSends} onSent={() => setCpaSendsKey(k => k + 1)} />
            )}
            {advisorMessages.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-stone-300 text-stone-700 hover:bg-stone-100"
                    data-testid="clear-history-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('chat.clear_history')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="clear-history-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear chat with {currentAdvisor?.name || 'this advisor'}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes your conversation with this advisor only. Chats with other advisors are kept. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="clear-history-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleClearHistory}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="clear-history-confirm"
                    >
                      Clear history
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Chat Container - Only show when advisor selected */}
        <Card className="flex-1 flex flex-col shadow-soft border-stone-200 overflow-hidden bg-white" data-testid="chat-container">
          <CpaShareStrip key={selectedAdvisor} sends={cpaSends} />
          <ScrollArea className="flex-1 p-3">
            {advisorMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 ${
                  queryMode === 'data' ? 'bg-blue-100' : 'bg-emerald-100'
                }`}>
                  {queryMode === 'data' 
                    ? <Database className="w-7 h-7 text-blue-500" />
                    : <MessageCircle className="w-7 h-7 text-emerald-500" />
                  }
                </div>
                <h2 className="text-lg font-semibold mb-1 text-stone-800 font-heading">
                  {queryMode === 'data' ? t('chat.query_your_data') : t('chat.ask_anything')}
                </h2>
                <p className="text-stone-600 mb-4 max-w-md text-sm">
                  {queryMode === 'data' 
                    ? t('chat.query_description')
                    : t('chat.get_personalized_advice')
                  }
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full max-w-4xl">
                  {(queryMode === 'data' ? DATA_QUERY_EXAMPLES : SUGGESTED_QUESTIONS).map((question, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      className="justify-start text-left h-auto py-2 px-3 bg-stone-50 border-stone-200 text-stone-700 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-colors group text-sm"
                      onClick={() => handleSuggestionClick(question)}
                      data-testid={`suggestion-${index}`}
                    >
                      {queryMode === 'data' 
                        ? <Search className="w-4 h-4 mr-2 flex-shrink-0 text-blue-500 group-hover:text-white" />
                        : <Sparkles className="w-4 h-4 mr-2 flex-shrink-0 text-emerald-500 group-hover:text-white" />
                      }
                      <span className="truncate">{question}</span>
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {advisorMessages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                    data-testid={`message-${index}`}
                  >
                    <div className={`max-w-[85%] ${
                      message.role === 'user'
                        ? message.mode === 'data' 
                          ? 'bg-blue-500 text-white rounded-2xl rounded-br-sm px-4 py-3'
                          : 'chat-bubble-user px-4 py-3'
                        : message.mode === 'data'
                          ? 'bg-blue-50 border border-blue-200 rounded-2xl rounded-bl-sm px-4 py-3'
                          : 'chat-bubble-ai px-4 py-3'
                    }`}>
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-stone-200">
                          {message.mode === 'data' ? (
                            <>
                              <Database className="w-5 h-5 text-blue-500" />
                              <span className="text-sm font-medium text-blue-700">{t('chat.data_query_result')}</span>
                              {message.dataYear && (
                                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full ml-auto">
                                  {t('chat.data_year', { year: message.dataYear })}
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <img 
                                src={ADVISOR_PHOTOS[message.advisorId || selectedAdvisor] || ADVISOR_PHOTOS.carlotta}
                                alt={getAdvisorInfo(message.advisorId || selectedAdvisor)?.name || 'Advisor'}
                                className="w-6 h-6 rounded-full object-cover"
                          />
                          <span className="text-xs font-medium text-stone-600" data-testid="message-advisor-name">{getAdvisorInfo(message.advisorId || selectedAdvisor)?.name || 'AI Advisor'}</span>
                            </>
                          )}
                        </div>
                      )}
                      <div className={`text-sm leading-relaxed ${
                        message.role === 'user' 
                          ? 'text-white' 
                          : 'text-stone-800'
                      }`}>
                        {/* Status indicator while streaming has no content yet */}
                        {message.isStreaming && !message.content && message.statusMsg && (
                          <div className="space-y-2">
                            <span className="flex items-center gap-2 text-stone-500 italic text-xs">
                              <span className="flex gap-0.5">
                                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                                <span className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                              </span>
                              {message.statusMsg}
                            </span>
                            {message.sources_preview && message.sources_preview.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 pt-0.5" data-testid="sources-preview-badges">
                                {message.sources_preview.map((src, i) => (
                                  <a
                                    key={i}
                                    href={src.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-xs text-emerald-700 font-medium hover:bg-emerald-100 transition-colors"
                                    data-testid={`preview-badge-${i}`}
                                  >
                                    <BookOpen className="w-2.5 h-2.5" />
                                    {src.pub_short}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {formatMessage(message.content)}
                        {message.isStreaming && message.content && (
                          <span className="inline-block w-0.5 h-4 bg-stone-500 ml-0.5 animate-pulse align-text-bottom" />
                        )}
                      </div>

                      {/* IRS Sources Panel */}
                      {message.role === 'assistant' && !message.isStreaming && message.sources && message.sources.length > 0 && (
                        <SourcesPanel sources={message.sources} />
                      )}

                      {/* Was this helpful? */}
                      {message.role === 'assistant' && !message.isStreaming && message.message_id && message.mode !== 'data' && (
                        <FeedbackBar messageId={message.message_id} apiHeaders={getAuthHeaders()} />
                      )}
                    </div>
                  </div>
                ))}
                
                {loading && (
                  <div className="flex justify-start animate-fade-in">
                    <div className={`px-4 py-3 rounded-2xl rounded-bl-sm ${
                      queryMode === 'data' ? 'bg-blue-50 border border-blue-200' : 'chat-bubble-ai'
                    }`}>
                      <div className="flex items-center gap-2">
                        {queryMode === 'data' ? (
                          <Database className="w-5 h-5 text-blue-500" />
                        ) : (
                          <img 
                            src={ADVISOR_PHOTOS[selectedAdvisor] || ADVISOR_PHOTOS.carlotta}
                            alt={currentAdvisor?.name || 'Advisor'}
                            className="w-6 h-6 rounded-full object-cover"
                          />
                        )}
                        <div className="flex gap-1">
                          <span className={`w-2 h-2 rounded-full typing-dot ${queryMode === 'data' ? 'bg-blue-400' : 'bg-emerald-500'}`}></span>
                          <span className={`w-2 h-2 rounded-full typing-dot ${queryMode === 'data' ? 'bg-blue-400' : 'bg-emerald-500'}`}></span>
                          <span className={`w-2 h-2 rounded-full typing-dot ${queryMode === 'data' ? 'bg-blue-400' : 'bg-emerald-500'}`}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="p-3 border-t border-stone-300 bg-slate-800">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('chat.ask_about', { topic: currentAdvisor?.expertise?.[0]?.toLowerCase() || 'tax strategies' })}
                disabled={loading}
                className="flex-1 bg-white border-stone-300 text-black placeholder:text-black focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="chat-input"
              />
              <Button type="submit" disabled={loading || !input.trim()} data-testid="send-btn">
                <Send className="w-4 h-4" />
              </Button>
            </form>
            <p className="text-xs mt-2 text-center font-medium" style={{ color: '#FFFFFF' }}>
              {currentAdvisor?.name || 'AI Advisor'} {t('chat.disclaimer')}
            </p>
          </div>
        </Card>
        </>
        )}
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="chat" />
  </>
  );
};

export default Chat;
